import moment from 'moment';

// Labels on this page are the API's enum vocabulary VERBATIM (see the
// HITCH-5500 contract): `normal | explore_off | frozen | safe`,
// `off | log_only | active`, etc. These maps pick only a pill colour (a palette
// key for ui/core/TintedStatusPill; null = neutral), never a friendlier label.

export const MODE_PALETTES = { off: null, log_only: 'warning', active: 'success' };

export const GUARD_STATE_PALETTES = { normal: 'success', explore_off: 'accent', frozen: 'warning', safe: 'error' };

export const RESPONSE_PALETTES = { alert: 'accent', explore_off: 'accent', freeze: 'warning', safe: 'error', refuse: 'error' };

export const LABEL_PALETTES = {
  ok: 'success',
  trouble: 'error',
  ahead: 'accent',
  below_min_board: 'warning',
  safe_mode: 'error',
};

export const SOURCE_PALETTES = {
  product_diff: 'accent',
  sdp_version: 'success',
  drift: null,
  manual: 'warning',
  guardrail: 'error',
  ownership: 'accent',
};

export const SHADOW_LABEL = 'shadow';

// log_only (and off) city-day rows carry multipliers SDP computed but never
// paid — legacy pricing set the pay. Only `active` cities are owned and paid.
export const shadowTooltip = (mode) => `${mode} — computed and logged, never paid`;

const EM_DASH = '—';
const MINUS = '−';

export const formatNum = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  if (typeof value !== 'number') return String(value);
  return String(Number(value.toFixed(digits)));
};

export const formatShare = (share) => (typeof share === 'number' ? `${Math.round(share * 100)}%` : EM_DASH);

export const formatTs = (value) => (value ? moment(value).format('MMM DD, hh:mm A') : EM_DASH);

export const formatDay = (value) => (value ? moment(value).format('MMM DD, YYYY') : EM_DASH);

export const formatJson = (value) => JSON.stringify(value === undefined ? null : value, null, 2);

const formatScalar = (value) => {
  if (value === null || value === undefined) return EM_DASH;
  if (typeof value === 'number') return formatNum(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

const isValuePrior = (cell) =>
  cell !== null && typeof cell === 'object' && !Array.isArray(cell) && Object.prototype.hasOwnProperty.call(cell, 'value');

// A readout cell is a scalar or `{ value, prior }`; when both are numbers we
// show the prior-window delta (signed; ±0 when unchanged).
export const formatCell = (cell) => {
  if (!isValuePrior(cell)) return { text: formatScalar(cell), delta: null };
  const { value, prior } = cell;
  const text = formatScalar(value);
  if (typeof value !== 'number' || typeof prior !== 'number') return { text, delta: null };
  const diff = Number((value - prior).toFixed(2));
  if (diff === 0) return { text, delta: '±0' };
  return { text, delta: diff > 0 ? `+${formatNum(diff)}` : `${MINUS}${formatNum(Math.abs(diff))}` };
};

export const DESCRIPTION_MAX = 500;
export const REASON_MAX = 500;
const EFFECTIVE_AT_MAX_AGE_DAYS = 400;

// Mirrors the API's validation of POST /policy-changes so the admin sees the
// problem before the 400.
export const validateManualPolicyChange = ({ effectiveAt, description, affects }, now = new Date()) => {
  const errors = {};
  if (!effectiveAt) {
    errors.effectiveAt = 'Effective at is required.';
  } else {
    const at = moment(effectiveAt);
    if (!at.isValid()) errors.effectiveAt = 'Effective at is not a valid date.';
    else if (at.isAfter(moment(now))) errors.effectiveAt = 'Effective at cannot be in the future.';
    else if (moment(now).diff(at, 'days', true) > EFFECTIVE_AT_MAX_AGE_DAYS)
      errors.effectiveAt = `Effective at cannot be more than ${EFFECTIVE_AT_MAX_AGE_DAYS} days ago.`;
  }
  const trimmed = (description || '').trim();
  if (!trimmed) errors.description = 'Description is required.';
  else if (trimmed.length > DESCRIPTION_MAX) errors.description = `Description must be at most ${DESCRIPTION_MAX} characters.`;
  if (!affects) errors.affects = 'Affects is required (global or a city).';
  return errors;
};

export const validateReason = (reason) => {
  const trimmed = (reason || '').trim();
  if (!trimmed) return 'A reason is required.';
  if (trimmed.length > REASON_MAX) return `Reason must be at most ${REASON_MAX} characters.`;
  return null;
};
